import { Stack, Typography } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react'
import {
  EOp,
  Nullish,
  Option,
  UserAdminRoles,
  UserOrganizationalRoles,
  UserType,
  UserTypeLabels,
  UserTypeTab,
} from 'types'

interface RoleOption {
  value: UserType
  label: string
}

export function useOrgRoleFilter(selectedTab: UserTypeTab, filterField: string) {
  const [rolesValue, setRolesValue] = useState<RoleOption[]>([])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rolesFilterValue: Nullish<any> = useMemo(() => {
    if (rolesValue.length > 0) {
      const filter = {
        or: [
          {
            [filterField]: {
              type: 'text',
              op: EOp.overlap,
              value: rolesValue.map((role) => role.value),
            },
          },
        ],
      }

      const hasUnassigned = rolesValue.some((role) => role.value === UserType.UNASSIGNED)
      if (hasUnassigned) {
        filter.or.push({
          [filterField]: {
            type: 'text',
            op: EOp.equals,
            value: [],
          },
        })
      }

      return filter
    }

    return null
  }, [rolesValue, filterField])

  const options = useMemo(() => {
    const adminTab = selectedTab === UserTypeTab.ADMIN
    if (adminTab) {
      return UserAdminRoles.map((role) => ({
        value: role,
        label: UserTypeLabels[role],
      }))
    }

    return UserOrganizationalRoles.map((role) => ({
      value: role,
      label: UserTypeLabels[role],
    })).concat({
      value: UserType.UNASSIGNED,
      label: 'Unassigned',
    })
  }, [selectedTab])

  const handleChange = useCallback((_: SyntheticEvent, newValue: Option[]) => {
    setRolesValue(newValue as RoleOption[])
  }, [])

  const RoleFilter = useMemo(() => {
    return (
      <Stack direction="row" alignItems="center" className="gap-8">
        <Typography>Role</Typography>

        <Autocomplete
          multiple
          options={options}
          value={rolesValue}
          onChange={handleChange}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              name="role"
              InputProps={{
                ...params.InputProps,
                endAdornment: <React.Fragment>{params.InputProps.endAdornment}</React.Fragment>,
              }}
            />
          )}
        />
      </Stack>
    )
  }, [handleChange, options, rolesValue])

  return { RoleFilter, rolesFilterValue, selectedTab }
}
